import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { AddPhotoAlternate as AddPhotoIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import { compressImage } from '../../utils/imageCompression';

interface AddItemDialogProps {
  open: boolean;
  onClose: () => void;
  onItemAdded: () => void;
  defaultCategory: string;
}

const AddItemDialog: React.FC<AddItemDialogProps> = ({
  open,
  onClose,
  onItemAdded,
  defaultCategory
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [minQuantityInput, setMinQuantityInput] = useState('5');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: defaultCategory,
    quantity: 0,
    unit: 'vnt',
    location: '',
    supplier: '',
    barkodas: '',
    prekesKodas: '',
    fabisKodas: '',
    savikaina: '',
    pardavimoKaina: ''
  });

  // Reset form when dialog opens
  React.useEffect(() => {
    if (!open) return;
    setFormData({
      name: '',
      description: '',
      category: defaultCategory,
      quantity: 0,
      unit: 'vnt',
      location: '',
      supplier: '',
      barkodas: '',
      prekesKodas: '',
      fabisKodas: '',
      savikaina: '',
      pardavimoKaina: ''
    });
    setMinQuantityInput('5');
    setImageUrl(null);
    setImagePreview(null);
    setError(null);
  }, [open]);

  // Update category when defaultCategory changes
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      category: defaultCategory
    }));
  }, [defaultCategory]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'category' && (value === 'exhibition' || value === 'discovery_space' || value === 'stickers')) {
        next.barkodas = '';
        next.prekesKodas = '';
        next.fabisKodas = '';
      }
      return next;
    });
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Pasirinkite paveikslėlio failą (JPEG, PNG, GIF, WebP)');
      return;
    }
    setError(null);
    setImageUploading(true);
    try {
      const compressed = await compressImage(file);
      const formDataUpload = new FormData();
      formDataUpload.append('image', compressed);
      const res = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_UPLOAD), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Nepavyko įkelti nuotraukos');
      }
      const data = await res.json();
      if (data.success && data.imageUrl) {
        setImageUrl(data.imageUrl);
        setImagePreview(`${API_BASE_URL}${data.imageUrl}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nepavyko įkelti nuotraukos');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(null);
    setImagePreview(null);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: defaultCategory,
      quantity: 0,
      unit: 'vnt',
      location: '',
      supplier: '',
      barkodas: '',
      prekesKodas: '',
      fabisKodas: '',
      savikaina: '',
      pardavimoKaina: ''
    });
    setMinQuantityInput('5');
    setImageUrl(null);
    setImagePreview(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.category) {
      setError('Pavadinimas ir kategorija yra privalomi');
      return;
    }

    if (formData.quantity < 0) {
      setError('Kiekis negali būti neigiamas');
      return;
    }

    const parsedMinQuantity = minQuantityInput.trim() === ''
      ? 5
      : parseInt(minQuantityInput, 10);

    if (isNaN(parsedMinQuantity) || parsedMinQuantity < 0) {
      setError('Minimalus kiekis turi būti neneigiamas skaičius');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_ITEMS), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          minQuantity: parsedMinQuantity,
          barkodas: (formData.category === 'exhibition' || formData.category === 'discovery_space' || formData.category === 'stickers')
            ? null
            : (formData.barkodas?.trim() || null),
          prekesKodas: formData.prekesKodas?.trim() || null,
          fabisKodas: formData.fabisKodas?.trim() || null,
          savikaina: formData.savikaina ? parseFloat(formData.savikaina as string) : null,
          pardavimoKaina: formData.pardavimoKaina ? parseFloat(formData.pardavimoKaina as string) : null,
          imageUrl: imageUrl || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create item');
      }

      // Reset form and close dialog
      resetForm();
      
      onItemAdded();
      onClose();
    } catch (error) {
      console.error('Error creating storage item:', error);
      setError(error instanceof Error ? error.message : 'Failed to create item');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
        ➕ Pridėti naują prekę
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
          {/* Name */}
          <TextField
            label="Prekės pavadinimas *"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
            required
            sx={{ gridColumn: 'span 2' }}
          />

          {/* Description */}
          <TextField
            label="Aprašymas"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ gridColumn: 'span 2' }}
          />

          {/* Optional image upload */}
          <Box sx={{ gridColumn: 'span 2' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Nuotrauka (neprivaloma)
            </Typography>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageChange}
              style={{ display: 'none' }}
              id="add-item-image-upload"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                component="label"
                htmlFor="add-item-image-upload"
                startIcon={<AddPhotoIcon />}
                disabled={imageUploading}
                sx={{ borderColor: '#7b1fa2', color: '#7b1fa2' }}
              >
                {imageUploading ? 'Kraunama...' : 'Įkelti nuotrauką'}
              </Button>
              {imagePreview && (
                <>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                  />
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveImage}
                  >
                    Pašalinti
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Category */}
          <FormControl fullWidth required>
            <InputLabel>Kategorija *</InputLabel>
            <Select
              value={formData.category}
              label="Kategorija *"
              onChange={(e) => handleInputChange('category', e.target.value)}
            >
              <MenuItem value="business">Ūkinės prekės</MenuItem>
              <MenuItem value="office">Kanceliarinės prekės</MenuItem>
              <MenuItem value="exhibition">Ekspozicija</MenuItem>
              <MenuItem value="discovery_space">Atradimų erdvė</MenuItem>
              <MenuItem value="souvenirs">Suvenyrai</MenuItem>
              <MenuItem value="stickers">Lipdukai</MenuItem>
              <MenuItem value="work_tools">Darbo reikmenys</MenuItem>
              <MenuItem value="workshop">Asmeniniai</MenuItem>
              <MenuItem value="kitchen">Virtuvėlė</MenuItem>
            </Select>
          </FormControl>

          {/* Unit */}
          <FormControl fullWidth>
            <InputLabel>Matavimo vienetas</InputLabel>
            <Select
              value={formData.unit}
              label="Matavimo vienetas"
              onChange={(e) => handleInputChange('unit', e.target.value)}
            >
              <MenuItem value="vnt">Vienetai (vnt)</MenuItem>
              <MenuItem value="kg">Kilogramai (kg)</MenuItem>
              <MenuItem value="m">Metrai (m)</MenuItem>
              <MenuItem value="l">Litrai (l)</MenuItem>
              <MenuItem value="pak">Pakuotės (pak)</MenuItem>
              <MenuItem value="kom">Komplektai (kom)</MenuItem>
            </Select>
          </FormControl>

          {/* Quantity */}
          <TextField
            label="Pradinis kiekis"
            type="number"
            value={formData.quantity}
            onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 0)}
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">{formData.unit}</InputAdornment>
            }}
          />

          {/* Min Quantity */}
          <TextField
            label="Minimalus kiekis"
            type="number"
            value={minQuantityInput}
            onChange={(e) => setMinQuantityInput(e.target.value)}
            fullWidth
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{formData.unit}</InputAdornment>
            }}
            helperText="Kiekis, kurį pasiekus bus rodomas įspėjimas"
          />

          {/* Location */}
          <TextField
            label="Sandėlio vieta"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            fullWidth
            placeholder="pvz.: A1, B2, C3"
          />



          {/* Supplier */}
          <TextField
            label="Tiekėjas"
            value={formData.supplier}
            onChange={(e) => handleInputChange('supplier', e.target.value)}
            fullWidth
          />

          {/* Savikaina (Cost Price) */}
          <TextField
            label="Savikaina (EUR)"
            type="number"
            value={formData.savikaina}
            onChange={(e) => handleInputChange('savikaina', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01', min: '0' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">EUR</InputAdornment>
            }}
            placeholder="0.00"
          />

          {/* Pardavimo kaina (Selling Price) */}
          <TextField
            label="Pardavimo kaina (EUR)"
            type="number"
            value={formData.pardavimoKaina}
            onChange={(e) => handleInputChange('pardavimoKaina', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01', min: '0' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">EUR</InputAdornment>
            }}
            placeholder="0.00"
          />

          {/* Barkodas - only show for souvenirs category */}
          {formData.category === 'souvenirs' && (
            <TextField
              label="Barkodas"
              value={formData.barkodas}
              onChange={(e) => handleInputChange('barkodas', e.target.value)}
              fullWidth
              placeholder="pvz.: 123456789012"
              sx={{ gridColumn: 'span 2' }}
            />
          )}
          {/* Prekės kodas - only show for souvenirs category */}
          {formData.category === 'souvenirs' && (
            <TextField
              label="Prekės kodas"
              value={formData.prekesKodas}
              onChange={(e) => handleInputChange('prekesKodas', e.target.value)}
              fullWidth
              placeholder="pvz.: Kojinės 000577"
              sx={{ gridColumn: 'span 2' }}
            />
          )}
          {formData.category === 'souvenirs' && (
            <TextField
              label="Fabis kodas"
              value={formData.fabisKodas}
              onChange={(e) => handleInputChange('fabisKodas', e.target.value)}
              fullWidth
              placeholder="pvz.: FAB-001"
              sx={{ gridColumn: 'span 2' }}
            />
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            * - privalomi laukai
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined" disabled={loading}>
          Atšaukti
        </Button>
        
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.name.trim() || !formData.category}
          sx={{ 
            backgroundColor: '#7b1fa2',
            '&:hover': { backgroundColor: '#6a1b9a' }
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Kuriama...
            </>
          ) : (
            'Pridėti prekę'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddItemDialog;

